import { getPlatformInfo } from '@/utils/platformDetection';
import { useKeepAwake } from 'expo-keep-awake';
import { useEffect, useRef } from 'react';
import { Platform } from 'react-native';

/**
 * Custom hook that safely handles wake lock requests with proper error handling
 * for web browsers where the WakeLock API has visibility requirements.
 * Completely disabled on iOS platforms (both native and web).
 * Code added as if call useKeepAwake on screen that is not visible in the browser, it will throw an error.
 */
export function useSafeKeepAwake(tag: string, options?: { suppressDeactivateWarnings?: boolean }) {
  const platformInfo = getPlatformInfo();
  const isIOS = platformInfo.isIOS;
  const isWeb = Platform.OS === 'web';
  const hasRequestedRef = useRef(false);

  // Completely disable wake lock on iOS platforms when in the browser
  if (isIOS && isWeb) {
    return;
  }

  useEffect(() => {
    if (!isWeb || hasRequestedRef.current) {
      return;
    }

    // Check if the page is visible before requesting wake lock
    const checkVisibilityAndRequestWakeLock = async () => {
      try {
        // Only request wake lock if the page is visible
        if (document.visibilityState === 'visible') {
          hasRequestedRef.current = true;
          // The actual wake lock request will be handled by useKeepAwake
          // but we'll catch any errors here
        }
      } catch (error) {
        console.warn('WakeLock API error (non-critical):', error);
        // Don't throw the error as this is not critical for app functionality
      }
    };

    checkVisibilityAndRequestWakeLock();

    // Listen for visibility changes to handle wake lock appropriately
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        // Page is hidden, wake lock will be automatically released
        hasRequestedRef.current = false;
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isWeb]);

  // Use the original useKeepAwake hook with error handling
  try {
    // Only call useKeepAwake if we're not on web or if page is visible
    if (!isWeb || (typeof document !== 'undefined' && document.visibilityState === 'visible')) {
      useKeepAwake(tag, options);
    }
  } catch (error) {
    // Catch any WakeLock API errors and log them as warnings
    if (error instanceof Error && error.name === 'NotAllowedError') {
      console.warn('WakeLock API not available or page not visible (non-critical):', error.message);
    } else {
      console.warn('WakeLock API error (non-critical):', error);
    }
    // Don't re-throw the error as this is not critical for app functionality
  }
}

/**
 * Alternative hook that completely avoids WakeLock API on web platforms
 * where it might cause issues, while still working on native platforms.
 */
export function useKeepAwakeNativeOnly(tag: string, options?: { suppressDeactivateWarnings?: boolean }) {
  // Only use keep awake on native platforms
  if (Platform.OS !== 'web') {
    useKeepAwake(tag, options);
  }
}
